import { Box, Typography } from "@mui/material";
import {
  Controller,
  Control,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import { CustomDateTimePicker } from "../../Calendar/CustomCalendar";
import { RequireIcon } from "@ippon/shared-assets";
import type { DateView } from "@mui/x-date-pickers";

export interface CustomCalendarInputProps<T extends FieldValues = FieldValues> {
  name: FieldPath<T>;
  control: Control<T, unknown, T>;
  label: string;
  isRequired?: boolean;
  rules?: RegisterOptions<T, FieldPath<T>>;
  /** ラベル領域の幅 (lg 以上)。未指定なら 100%。 */
  labelWidth?: string;
  /** ラベル領域の幅 (xs)。未指定なら 100%。 */
  labelWidthSp?: string;
  /** 入力領域の最大幅 (sm 以上)。未指定なら 100%。 */
  inputWidth?: string;
  /** 入力領域の最大幅 (xs)。未指定なら 100%。 */
  inputWidthSp?: string;
  /** 下マージン。横並びの日付範囲などでは 0 を指定。未指定時は theme spacing 2 */
  marginBottom?: number | string;
  /** ラベル文字列の右隣に挟む任意の React ノード (AI バッジ等) */
  labelSuffix?: React.ReactNode;
  /** DatePicker の表示単位。年月入力では ["year", "month"] を指定する。 */
  views?: DateView[];
  /** 開いた直後に表示する単位。年月入力で月を先に出す場合は "month"。 */
  openTo?: DateView;
  /** 入力欄の表示フォーマット。未指定時は日付表示。 */
  format?: string;
  /** onChange でフォームへ渡すフォーマット。未指定時は YYYY-MM-DD。 */
  outputFormat?: string;
}

const CustomCalendarInput = <T extends FieldValues>({
  name,
  control,
  label,
  isRequired = false,
  rules,
  labelWidth,
  inputWidth,
  labelWidthSp,
  inputWidthSp,
  marginBottom,
  labelSuffix,
  views,
  openTo,
  format,
  outputFormat,
}: CustomCalendarInputProps<T>) => {
  const finalRules: RegisterOptions<T, FieldPath<T>> | undefined =
    isRequired && rules?.required === undefined
      ? { ...rules, required: `${label}は必須です` }
      : rules;

  const showLabelRow = Boolean(label) || isRequired;

  return (
    <Box
      // useScrollToFirstError がフィールド名で DOM を引けるように。
      // Controller 内部の DateTimePicker には ref / name が届かないため、
      // 外側のラッパに付与してスクロールターゲットとして使う。
      data-rhf-field={name}
      sx={{
        mb: marginBottom !== undefined ? marginBottom : 2,
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {showLabelRow && (
        <Typography
          fontSize={12}
          fontWeight={700}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            width: { lg: labelWidth ?? "100%", xs: labelWidthSp ?? "100%" },
            mb: 0.5,
          }}
        >
          {label}
          {isRequired && (
            <Box component="span" sx={{ display: "inline-flex" }}>
              <RequireIcon />
            </Box>
          )}
          {labelSuffix}
        </Typography>
      )}
      <Box
        sx={{
          width: "100%",
          maxWidth: { sm: inputWidth ?? "100%", xs: inputWidthSp ?? "100%" },
        }}
      >
        <Controller
          name={name}
          control={control as any}
          rules={finalRules}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomDateTimePicker
              onChange={onChange}
              width="100%"
              value={value || undefined}
              defaultValue={null}
              showTime={false}
              error={!!error}
              helperText={error?.message}
              views={views}
              openTo={openTo}
              format={format}
              outputFormat={outputFormat}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default CustomCalendarInput;
